// /opt/isp_erp/frontend/src/pages/warehouse/Inventory.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Alert
} from '@mui/material';
import { Search, Refresh, ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { warehouseAPI } from '../../api/warehouse.api';

const Inventory = () => {
  const [equipos, setEquipos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('todos');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const peticionRef = useRef(0);

  // Espera 400 ms después de escribir antes de consultar al servidor
  useEffect(() => {
    const t = setTimeout(() => {
      setBusqueda(searchTerm.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const cargarInventario = useCallback(async () => {
    const id = ++peticionRef.current;
    setLoading(true);
    setError('');
    try {
      const response = await warehouseAPI.listarEquiposPaginado({
        q: busqueda || undefined,
        estado: estadoFilter !== 'todos' ? estadoFilter : undefined,
        page: page + 1,
        page_size: rowsPerPage,
        orden: orderBy,
        direccion: order
      });
      if (id !== peticionRef.current) return; // llegó una respuesta vieja
      setEquipos(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      if (id !== peticionRef.current) return;
      console.error('Error cargando inventario:', err);
      const detalle = err.response?.data?.detail;
      setError(typeof detalle === 'string' ? detalle : 'Error al cargar el inventario');
      setEquipos([]);
      setTotal(0);
    } finally {
      if (id === peticionRef.current) setLoading(false);
    }
  }, [busqueda, estadoFilter, page, rowsPerPage, orderBy, order]);

  useEffect(() => {
    cargarInventario();
  }, [cargarInventario]);

  const getEstadoColor = (estado) => {
    const colors = {
      BODEGA: 'success',
      EN_TRANSITO: 'warning',
      INSTALADO: 'info',
      RETIRO_PENDIENTE: 'error',
      RMA_BAJA: 'default'
    };
    return colors[estado] || 'default';
  };

  const getEstadoTexto = (estado) => {
    const textos = {
      BODEGA: 'En Bodega',
      EN_TRANSITO: 'En Tránsito',
      INSTALADO: 'Instalado',
      RETIRO_PENDIENTE: 'Retiro Pendiente',
      RMA_BAJA: 'RMA/Baja'
    };
    return textos[estado] || estado;
  };

  const handleSort = (field) => {
    const isAsc = orderBy === field && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(field);
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Inventario de Bodega
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Buscar por SN o Modelo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={estadoFilter}
                  onChange={(e) => {
                    setEstadoFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Estado"
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="BODEGA">En Bodega</MenuItem>
                  <MenuItem value="EN_TRANSITO">En Tránsito</MenuItem>
                  <MenuItem value="INSTALADO">Instalado</MenuItem>
                  <MenuItem value="RETIRO_PENDIENTE">Retiro Pendiente</MenuItem>
                  <MenuItem value="RMA_BAJA">RMA/Baja</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={cargarInventario}
                startIcon={<Refresh />}
              >
                Actualizar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSort('sn')}
                  endIcon={orderBy === 'sn' && (order === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
                >
                  SN
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSort('modelo')}
                  endIcon={orderBy === 'modelo' && (order === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
                >
                  Modelo
                </Button>
              </TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Poseedor</TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSort('created_at')}
                  endIcon={orderBy === 'created_at' && (order === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
                >
                  Fecha Ingreso
                </Button>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Cargando...
                </TableCell>
              </TableRow>
            ) : equipos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No hay equipos registrados
                </TableCell>
              </TableRow>
            ) : (
              equipos.map((equipo) => (
                <TableRow key={equipo.id} hover>
                  <TableCell>
                    <strong>{equipo.sn}</strong>
                  </TableCell>
                  <TableCell>{equipo.modelo}</TableCell>
                  <TableCell>
                    <Chip
                      label={getEstadoTexto(equipo.estado_actual)}
                      color={getEstadoColor(equipo.estado_actual)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{equipo.poseedor?.nombre || 'Bodega'}</TableCell>
                  <TableCell>
                    {new Date(equipo.created_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>
    </Box>
  );
};

export default Inventory;
